import Layout from '@components/layout'
import { shopPageModuleQuery } from '@data/sanity/queries/page'
import { type SanityShopPage } from '@data/sanity/queries/types/page'
import { type SanitySiteFragment } from '@data/sanity/queries/types/site'
import { type Locale, getAllLocales } from '@lib/language'
import {
  type BasePageProps,
  getNonNullValues,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import { type CustomStaticPropsContext, getPageUrl } from '@lib/routes'
import { getSanityClient } from '@lib/sanity/client'
import {
  defaultModuleQueryParameters,
  getShopIndexPage,
  getShopPagePaths,
} from '@lib/sanity/page'
import { getVideoObjectSchemas } from '@lib/schema/video'
import { getShopifyShopForPageWithCache } from '@lib/shopify/store-cache'
import Modules from '@modules/modules'
import { type GetStaticPathsResult, type GetStaticPropsResult } from 'next'
import dynamic from 'next/dynamic'
import { type ParsedUrlQuery } from 'querystring'
import { useMemo } from 'react'

const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type ShopChildPageProps = BasePageProps & {
  childSlug: string
  page: SanityShopPage
  site: SanitySiteFragment
}

type StaticShopChildPageParams = ParsedUrlQuery & {
  childSlug: string
}

const ShopChildPage = ({
  draftMode,
  locale,
  childSlug,
  page,
  site,
}: ShopChildPageProps) => {
  const canonicalUrl = useMemo(() => {
    const domain = site.generalSettings.siteURL.replace(/\/+$/g, '')
    const pageUrl = getPageUrl(locale, 'shopPageChild', childSlug)

    return `${domain}${pageUrl}`
  }, [childSlug, locale, site])

  const schemas = useMemo(() => {
    const videoObjectSchemas = getVideoObjectSchemas(page)
    return [...videoObjectSchemas]
  }, [page])

  return (
    <Layout
      page={page}
      site={site}
      canonicalUrl={canonicalUrl}
      schemas={schemas}
      draftMode={draftMode}
    >
      {!draftMode && (
        <Modules
          modules={page.modules}
          pageProducts={page.products}
          pageFeaturedProductIds={page.featuredProductIds}
          parentPageType="shopPage"
          childSlug={childSlug}
          quickFilterBasePath="/shop/all"
          quickFilterAllPath="/shop"
        />
      )}
      {draftMode && (
        <PreviewModules
          modules={page.modules}
          query={shopPageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
          }}
          pageProducts={page.products}
          pageFeaturedProductIds={page.featuredProductIds}
          parentPageType="shopPage"
          childSlug={childSlug}
          quickFilterBasePath="/shop/all"
          quickFilterAllPath="/shop"
        />
      )}
    </Layout>
  )
}

export async function getStaticProps(
  context: CustomStaticPropsContext<StaticShopChildPageParams>,
): Promise<GetStaticPropsResult<ShopChildPageProps>> {
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const shopIndexPage = await getShopIndexPage(sanityClient, context.locale)

  if (!shopIndexPage.page) {
    return {
      notFound: true,
      revalidate: 600,
    }
  }

  const childSlug = context.params?.childSlug ?? ''

  const shopifyShop = await getShopifyShopForPageWithCache(context.locale)

  return {
    props: {
      ...getPagePropsFromContext(context),
      childSlug,
      page: getNonNullValues(shopIndexPage.page),
      site: getNonNullValues(shopIndexPage.site),
      shopifyShop,
    },
    revalidate: 600,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<StaticShopChildPageParams>
> {
  if (process.env.NEXT_PUBLIC_APP_ENV === 'preview') {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }

  const sanityClient = getSanityClient()
  const allLocales = getAllLocales()
  const shopPagePaths = await getShopPagePaths(
    sanityClient,
    allLocales,
    'shopPage',
  )

  const paths = shopPagePaths
    .filter((shopPage) => {
      return allLocales.includes(shopPage.locale as Locale)
    })
    .flatMap((shopPage) => {
      return shopPage.modules.flatMap((module) => {
        const featuredCollections = module.featuredCollections ?? []

        return featuredCollections
          .filter((featuredCollection) => {
            return !!featuredCollection.slug.current
          })
          .map((featuredCollection) => {
            return {
              params: {
                childSlug: featuredCollection.slug.current,
              },
              locale: shopPage.locale,
            }
          })
      })
    })

  return {
    paths,
    fallback: 'blocking',
  }
}

export default ShopChildPage
